import { useId } from "@reach/auto-id";
import { hideOthers } from "aria-hidden";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import exenv from "exenv";
import React, { createContext, forwardRef, useCallback, useContext, useEffect, useRef } from "react";
import FocusLock from "react-focus-lock";
import Box, { BoxProps } from "../box/Box";
import CloseButton, { ICloseButton } from "../CloseButton";
import Portal from "../portal/Portal";
import { getFocusables, useForkRef, wrapEvent } from "../utils";
import { RemoveScroll } from "react-remove-scroll";

type ModalSizes = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "full";

export interface IModal {
  container?: React.RefObject<HTMLElement>;
  /**
   * If `true`, the modal when be opened.
   */
  isOpen?: boolean;
  /**
   * Callback invoked to close the modal.
   */
  onClose?: (event: React.MouseEvent | React.KeyboardEvent, reason?: "pressedEscape" | "clickedOverlay") => void;
  /**
   * If `true`, scrolling will be disabled on the `body` when the modal opens.
   *  @default true
   */
  blockScrollOnMount?: boolean;
  /**
   * A11y: If `true`, the siblings of the `Modal` will have `aria-hidden`
   * set to `true` so that screen readers can only see the `Modal`.
   *
   * This is commonly known as making the other elements **inert**
   *
   *  @default true
   */
  useInert?: boolean;
  /**
   * If `true`, a `padding-right` will be applied to the body element
   * that's equal to the width of the scrollbar.
   *
   * This can help prevent some unpleasant flickering effect
   * and content adjustment when the modal opens
   */
  preserveScrollBarGap?: boolean;
  /**
   * The content of the modal.
   */
  children: React.ReactNode;
  /**
   * The size (maxWidth) of the modal.
   */
  size?: ModalSizes | BoxProps["maxWidth"];
  /**
   *  If `true`, the modal will be centered on screen.
   */
  isCentered?: boolean;
  /**
   * The `ref` of element to receive focus when the modal opens.
   */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /**
   * The `ref` of element to receive focus when the modal closes.
   */
  finalFocusRef?: React.RefObject<HTMLElement>;
  /**
   * Where scroll behaviour should originate.
   * - If set to `inside`, scroll only occurs within the `ModalBody`.
   * - If set to `outside`, the entire `ModalContent` will scroll within the viewport.
   */
  scrollBehavior?: "inside" | "outside";
  /**
   * If `true`, the modal will close when the overlay is clicked
   * @default true
   */
  closeOnOverlayClick?: boolean;
  /**
   * If `true`, the modal will close when the `Esc` key is pressed
   * @default true
   */
  closeOnEsc?: boolean;
  /**
   * The `id` of the modal
   */
  id?: string;
  /**
   * If `true`, the modal will return focus to the element that triggered it when it closes.
   */
  returnFocusOnClose?: boolean;
  /**
   * By default, a unique `id` is passed to the header and body.
   * These ids are used to add `aria-labelledby` and `aria-describedby` to the `ModalContent`.
   *
   * You can configure this behavior:
   * - Set it to `false` if you'd like to manually add the `aria-*` attributes.
   * - Set it to `{header: false}` if you don't render the `ModalHeader` within the modal.
   * We'll remove the `aria-labelledby` prop.
   *
   * @default true
   */
  addAriaLabels?: boolean | { header?: boolean; body?: boolean };
  /**
   * The function to format the `id`s passed to the `ModalHeader`, `Modalbody`, and `ModalContent`
   */
  formatIds?: (id: string | number) => { content: string; header: string; body: string };
}

interface IModalContent extends Omit<BoxProps, "onClick"> {
  onClick?: React.KeyboardEventHandler<HTMLElement>;
  zIndex?: BoxProps["zIndex"];
  children: React.ReactNode;
  noStyles?: boolean;
}

interface IModalContext {
  isOpen?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
  onClose?: (event: React.MouseEvent | React.KeyboardEvent, reason?: "pressedEscape" | "clickedOverlay") => void;
  blockScrollOnMount?: boolean;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
  returnFocusOnClose?: boolean;
  contentRef?: React.MutableRefObject<null>;
  scrollBehavior?: "inside" | "outside";
  isCentered?: boolean;
  headerId?: string;
  bodyId?: string;
  contentId?: string;
  size?: ModalSizes | BoxProps["maxWidth"];
  addAriaLabelledby?: boolean;
  addAriaDescribedby?: boolean;
}

const { canUseDOM } = exenv;
const ModalContext = createContext({});
const useModalContext = () => useContext<IModalContext>(ModalContext);

interface IAriaHiderProps {
  isOpen?: boolean;
  id: string;
  enableInert: boolean;
  container?: HTMLElement | null;
}

function useAriaHider({ isOpen, id, enableInert, container = canUseDOM ? document.body : null }: IAriaHiderProps) {
  const mountRef = useRef<HTMLDivElement | null>(
    canUseDOM ? (document.getElementById(id) as HTMLDivElement) || document.createElement("div") : null,
  );

  useEffect(() => {
    let undoAriaHidden: any = null;
    const mountNode = mountRef.current;

    if (isOpen && canUseDOM && mountRef && mountRef.current) {
      mountRef.current.id = id;
      if (container && typeof container.appendChild === "function") {
        container.appendChild(mountRef.current);
      }
      if (enableInert && mountNode) {
        undoAriaHidden = hideOthers(mountNode);
      }
    }

    return () => {
      if (enableInert && undoAriaHidden != null) {
        undoAriaHidden();
      }
      if (mountNode && mountNode.parentElement) {
        mountNode.parentElement.removeChild(mountNode);
      }
    };
  }, [isOpen, id, enableInert, container]);

  return mountRef;
}

const Modal: React.FC<IModal> = ({
  isOpen,
  initialFocusRef,
  finalFocusRef,
  onClose,
  blockScrollOnMount = true,
  closeOnEsc = true,
  closeOnOverlayClick = true,
  useInert = true,
  scrollBehavior = "outside",
  isCentered,
  addAriaLabels = true,
  preserveScrollBarGap,
  formatIds = id => ({
    content: `modal-${id}`,
    header: `modal-${id}-header`,
    body: `modal-${id}-body`,
  }),
  container,
  returnFocusOnClose = true,
  children,
  id,
  size = "md",
}) => {
  const contentRef = useRef<HTMLElement | null>(null);
  const uuid = useId();
  const _id = id || uuid || "";

  const contentId = formatIds(_id).content;
  const headerId = formatIds(_id).header;
  const bodyId = formatIds(_id).body;
  const portalId = `chakra-portal-${_id}`;

  let addAriaLabelledby = false;
  let addAriaDescribedby = false;

  if (typeof addAriaLabels === "object") {
    addAriaLabelledby = addAriaLabels.header || false;
    addAriaDescribedby = addAriaLabels.body || false;
  }

  if (typeof addAriaLabels === "boolean") {
    addAriaLabelledby = addAriaLabels;
    addAriaDescribedby = addAriaLabels;
  }

  useEffect(() => {
    const dialogNode = contentRef.current;

    if (isOpen && blockScrollOnMount && dialogNode) {
      disableBodyScroll(dialogNode, {
        reserveScrollBarGap: preserveScrollBarGap,
      });
    }
    return () => enableBodyScroll((dialogNode as any) || document.body );
  }, [isOpen, blockScrollOnMount, preserveScrollBarGap]);

  useEffect(() => {
    const func = (event: any) => {
      if (event.key === "Escape" && closeOnEsc && onClose) {
        onClose(event, "pressedEscape");
      }
    };

    if (isOpen && !closeOnOverlayClick) {
      canUseDOM && document.addEventListener("keydown", func);
    }
    return () => {
      canUseDOM && document.removeEventListener("keydown", func);
    };
  }, [isOpen, onClose, closeOnOverlayClick, closeOnEsc]);

  const mountRef = useAriaHider({
    isOpen,
    id: portalId,
    enableInert: useInert,
    container: container as any,
  });

  const context = {
    isOpen,
    initialFocusRef,
    onClose,
    blockScrollOnMount,
    closeOnEsc,
    closeOnOverlayClick,
    returnFocusOnClose,
    contentRef,
    scrollBehavior,
    isCentered,
    headerId,
    bodyId,
    contentId,
    size,
    addAriaLabelledby,
    addAriaDescribedby,
  };

  const activateFocusLock = useCallback(() => {
    if (initialFocusRef && initialFocusRef.current) {
      initialFocusRef.current.focus();
    } else if (contentRef.current) {
      const focusables = getFocusables(contentRef.current);
      if (focusables.length === 0) {
        (contentRef.current as any).focus();
      }
    }
  }, [initialFocusRef]);

  const deactivateFocusLock = useCallback(() => {
    if (finalFocusRef && finalFocusRef.current) {
      finalFocusRef.current.focus();
    }
  }, [finalFocusRef]);

  if (!isOpen) return null;

  return (
    <ModalContext.Provider value={context}>
      <Portal container={mountRef.current}>
        <FocusLock
          returnFocus={returnFocusOnClose && !finalFocusRef}
          onActivation={activateFocusLock}
          onDeactivation={deactivateFocusLock}
        >
          <RemoveScroll enabled={blockScrollOnMount}>{children}</RemoveScroll>
        </FocusLock>
      </Portal>
    </ModalContext.Provider>
  );
};

const ModalOverlay: React.FC<BoxProps> = React.forwardRef<HTMLDivElement, BoxProps>((props, ref) => {
  return (
    <Box
      pos="fixed"
      bg="rgba(0,0,0,0.4)"
      left="0"
      top="0"
      w="100vw"
      h="100vh"
      ref={ref}
      zIndex={"overlay" as any}
      onClick={wrapEvent(props.onClick, event => {
        event.stopPropagation();
      })}
      {...props}
    />
  );
});

ModalOverlay.displayName = "ModalOverlay";

const ModalContent: React.FC<IModalContent> = React.forwardRef<HTMLDivElement, IModalContent>(
  ({ onClick, children, zIndex = "modal", noStyles, ...props }, ref) => {
    const {
      contentRef,
      onClose,
      isCentered,
      bodyId,
      headerId,
      contentId,
      size,
      closeOnEsc,
      addAriaLabelledby,
      addAriaDescribedby,
      scrollBehavior,
      closeOnOverlayClick,
    } = useModalContext();
    const _contentRef = useForkRef(ref, contentRef);

    const boxStyleProps = {
      bg: "white",
      shadow: "0 7px 14px 0 rgba(0,0,0, 0.1), 0 3px 6px 0 rgba(0, 0, 0, .07)",
    };

    let wrapperStyle = {};
    let contentStyle = {};

    if (isCentered) {
      wrapperStyle = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      };
    } else {
      contentStyle = {
        top: "3.75rem",
        mx: "auto",
      };
    }

    if (scrollBehavior === "inside") {
      wrapperStyle = {
        ...wrapperStyle,
        maxHeight: "calc(100vh - 7.5rem)",
        overflow: "hidden",
        top: "3.75rem",
      };

      contentStyle = {
        ...contentStyle,
        height: "100%",
        top: 0,
      };
    }

    if (scrollBehavior === "outside") {
      wrapperStyle = {
        ...wrapperStyle,
        overflowY: "auto",
        overflowX: "hidden",
      };

      contentStyle = {
        ...contentStyle,
        my: "3.75rem",
        top: 0,
      };
    }

    if (noStyles) {
      wrapperStyle = {};
      contentStyle = {};
    }

    return (
      <Box
        pos="fixed"
        left="0"
        top="0"
        w="100%"
        h="100%"
        as="div"
        ref={ref}
        zIndex={zIndex as any}
        onClick={event => {
          event.stopPropagation();
          if (closeOnOverlayClick && onClose) {
            onClose(event, "clickedOverlay");
          }
        }}
        onKeyDown={event => {
          if (event.key === "Escape") {
            event.stopPropagation();
            if (closeOnEsc && onClose) {
              onClose(event, "pressedEscape");
            }
          }
        }}
        {...wrapperStyle}
      >
        <Box
          ref={_contentRef}
          as="section"
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          outline={0}
          maxWidth={size}
          w="100%"
          id={contentId}
          {...(addAriaDescribedby && { "aria-describedby": bodyId })}
          {...(addAriaLabelledby && { "aria-labelledby": headerId })}
          pos="relative"
          d="flex"
          flexDir="column"
          zIndex={zIndex as any}
          onClick={wrapEvent(onClick, event => event.stopPropagation())}
          {...boxStyleProps}
          {...contentStyle}
          {...props}
        >
          {children}
        </Box>
      </Box>
    );
  },
);

ModalContent.displayName = "ModalContent";

const ModalHeader: React.FC<BoxProps> = forwardRef<HTMLDivElement, BoxProps>((props, ref) => {
  const { headerId } = useModalContext();
  return (
    <Box
      ref={ref}
      px={6}
      py={4}
      id={headerId}
      as="header"
      position="relative"
      fontSize="xl"
      fontWeight="semibold"
      {...props}
    />
  );
});

ModalHeader.displayName = "ModalHeader";

const ModalFooter: React.FC<BoxProps> = forwardRef<HTMLDivElement, BoxProps>((props, ref) => (
  <Box display="flex" justifyContent="flex-end" ref={ref} px={6} py={4} as="footer" {...props} />
));

ModalFooter.displayName = "ModalFooter";

const ModalBody: React.FC<BoxProps> = forwardRef<HTMLDivElement, BoxProps>((props, ref) => {
  const { bodyId, scrollBehavior } = useModalContext();

  let style = {};
  if (scrollBehavior === "inside") {
    style = { overflowY: "auto" };
  }

  return <Box ref={ref} id={bodyId} px={6} py={2} flex="1" {...style} {...props} />;
});

ModalBody.displayName = "ModalBody";

type ModalCloseButtonProps = ICloseButton & Omit<BoxProps, "size">;

const ModalCloseButton: React.FC<ModalCloseButtonProps> = forwardRef<HTMLElement, ModalCloseButtonProps>(
  (props, ref) => {
    const { onClose } = useModalContext();
    return <CloseButton ref={ref} onClick={onClose} position="absolute" top="8px" right="12px" {...props} />;
  },
);

ModalCloseButton.displayName = "ModalCloseButton";

export { Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton };
